<template>
  <Modal v-bind="$attrs" v-on="$listeners">
    <SfHeading :level="3" :title="$t('Vehicle search')" class="sf-heading--left" />
    <SfDivider />
    <form @submit.prevent="submitFilter" class="row">
      <div class="col-xs-12">
        <SfHeading
          :level="4"
          :title="$t('Select Brand')"
          class="sf-heading--left"
        />
        <div class="row">
          <div
            v-for="(filter, index) in basic"
            :key="filter.valueName"
            class="col-xs-12 col-sm-4"
          >
            <SfSelect
              :key="generateKeyBaseOnOptions(filter)"
              :label="$t(filter.label)"
              :placeholder="$t('Select option')"
              :selected="vehicleCurrentFilters[filter.valueName]"
              :disabled="isDisabled(filter.name)"
              :value="getCurrentValue(filter.valueName)"
              @input="handleChange($event, basic, filter, index, true)"
            >
              <SfSelectOption
                v-for="option in getOptions(filter.name)"
                :key="option.value"
                :value="option.value"
              >{{ $t(option.name) }}</SfSelectOption>
            </SfSelect>
          </div>
        </div>
      </div>
      <div class="col-xs-12">
        <SfHeading
          :level="4"
          :title="$t('Select Vehicle')"
          class="sf-heading--left"
        />
        <div class="row">
          <div
            v-for="(filter, index) in vehicleSelectedFilters"
            :key="filter.valueName"
            class="col-xs-12 col-sm-4"
          >
            <SfSelect
              :key="generateKeyBaseOnOptions(filter)"
              :label="$t(filter.label)"
              :placeholder="$t('Select option')"
              :selected="filter.isArray && vehicleCurrentFilters[filter.valueName] ?
                vehicleCurrentFilters[filter.valueName][0] :
                vehicleCurrentFilters[filter.valueName]"
              :disabled="isDisabled(filter.name)"
              :value="getCurrentValue(filter.valueName)"
              @input="handleChange($event, vehicleSelectedFilters, filter, index)"
            >
              <SfSelectOption
                v-for="option in getOptions(filter.name)"
                :key="option.value"
                :value="option.value"
              >{{ option.name }}</SfSelectOption>
            </SfSelect>
          </div>
        </div>
      </div>
      <div class="col-xs-12">
        <div class="actions">
          <SfButton
            tyep="submit"
            :disabled="!submitEnabled"
          >
            {{ $t('Search') }}
          </SfButton>
        </div>
      </div>
    </form>
  </Modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { Modal } from '@/modules/core';
import {
  SfButton,
  SfSelect,
  SfHeading,
  SfDivider,
} from '@storefront-ui/vue';

import {
  mapFilterOptionsToGettersMaps,
  optionsName,
  optionsMutationName,
  mapOptions,
} from '../../utils/vehicleFilters';
import {
  vehicleFiltersBasicTypes,
  vehicleFiltersVehicleTypes,
} from '../../constants';

export default {
  name: 'SearchByFiltersModal',
  components: {
    Modal,
    SfSelect,
    SfHeading,
    SfDivider,
    SfButton,
  },
  data() {
    return {
      basic: vehicleFiltersBasicTypes,
      vehicle: vehicleFiltersVehicleTypes,
    };
  },
  computed: {
    vehicleSelectedFilters() {
      const { category } = this.vehicleCurrentFilters;
      if (category) {
        const filterType = category === 'PKW' ? 'isAuto' : 'isMotor';
        return this.vehicle.filter((item) => item[filterType]);
      }
      return this.vehicle;
    },
    submitEnabled() {
      return !!this.vehicleCurrentFilters.month;
    },
    ...mapGetters({
      vehicleCurrentFilters: 'level4/vehicleCurrentFilters',
      defaultVehicleFilters: 'level4/defaultVehicleFilters',
      ...mapFilterOptionsToGettersMaps(vehicleFiltersBasicTypes),
      ...mapFilterOptionsToGettersMaps(vehicleFiltersVehicleTypes),
    }),
  },
  methods: {
    submitFilter() {
      this.searchByFilter();
      this.$emit('close');
    },
    generateKeyBaseOnOptions(filter) {
      return filter.valueName + this.getOptions(filter.name).map((item) => item.uuid).join('-');
    },
    getCurrentValue(valueName) {
      return this.vehicleCurrentFilters[valueName] || '';
    },
    handleChange(value, filterType, filter, index, clearAllVehicle) {
      const { isArray, valueName: name } = filter;
      const filtersToClear = [
        ...filterType.slice(index + 1),
        ...(() => (clearAllVehicle ? this.vehicle : []))(),
      ];

      this.setVehicleCurrentFilters({
        [name]: isArray ? [value] : value,
      });

      filtersToClear.forEach((item) => {
        this.setVehicleCurrentFilters({
          [item.valueName]: this.defaultVehicleFilters[item.valueName],
        });
        this.$store.commit(`level4/${optionsMutationName(item.name)}`, []);
      });

      if (index + 1 < filterType.length) {
        this.fetchVehicleFilter({
          filterType: filterType[index + 1].name,
        });
      }

      if (name === 'catalog') {
        this.fetchVehicleFilter({
          filterType: this.vehicleSelectedFilters[0].name,
        });
      }
    },
    getOptions(filterName) {
      return mapOptions(this[optionsName(filterName)]);
    },
    isDisabled(filterName) {
      return !this.getOptions(filterName).length;
    },
    ...mapActions({
      setVehicleCurrentFilters: 'level4/setVehicleCurrentFilters',
      fetchVehicleFilter: 'level4/fetchVehicleFilter',
      searchByFilter: 'level4/searchByFilter',
    }),
  },
};
</script>

<style lang="scss" scoped>
@import "~@storefront-ui/shared/styles/_helpers";
@import "src/scss/_breakpoint";

.sf-heading,
.sf-divider {
  margin-bottom: var(--spacer-sm);
}

.sf-modal {
  --modal-width: auto;
  @include flexbox-desktop {
    --modal-height: 95vh;

    /deep/ .sf-modal__content {
      height: 100%;
    }
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin: var(--spacer-sm) 0;
}
</style>
